import { useEffect } from "react";

const FOCUSABLE_SELECTOR = [
  "button:not([disabled])",
  "a[href]",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])"
].join(",");

const getFocusable = (container) => {
  if (!container) return [];
  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter((node) => {
    if (!(node instanceof HTMLElement)) return false;
    return node.offsetParent !== null || node === document.activeElement;
  });
};

export function useModalA11y({ isOpen, onClose, containerRef, initialFocusRef }) {
  useEffect(() => {
    if (!isOpen) return undefined;

    const previousActive = document.activeElement;

    const focusInitial = () => {
      const initial = initialFocusRef?.current;
      if (initial && typeof initial.focus === "function") {
        initial.focus();
        return;
      }
      const focusables = getFocusable(containerRef?.current);
      if (focusables.length > 0 && typeof focusables[0].focus === "function") {
        focusables[0].focus();
      }
    };

    const timeout = window.setTimeout(focusInitial, 20);

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        if (typeof onClose === "function") onClose();
        return;
      }

      if (event.key !== "Tab") return;
      const container = containerRef?.current;
      const focusables = getFocusable(container);
      if (focusables.length === 0) {
        event.preventDefault();
        return;
      }

      const currentIndex = focusables.indexOf(document.activeElement);
      if (event.shiftKey) {
        if (currentIndex <= 0) {
          event.preventDefault();
          focusables[focusables.length - 1].focus();
        }
      } else if (currentIndex === focusables.length - 1) {
        event.preventDefault();
        focusables[0].focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      window.clearTimeout(timeout);
      document.removeEventListener("keydown", handleKeyDown);
      if (previousActive instanceof HTMLElement && typeof previousActive.focus === "function") {
        previousActive.focus();
      }
    };
  }, [isOpen, onClose, containerRef, initialFocusRef]);
}
